package ru.chm.gradereport.ui.screens.course.sheet

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.window.Dialog
import ru.chm.gradereport.model.HomeworkSubmissionItemDto
import ru.chm.gradereport.ui.components.AppCard
import ru.chm.gradereport.ui.components.MutedText
import ru.chm.gradereport.ui.components.SecondaryButton
import ru.chm.gradereport.ui.components.rememberSoftHover
import ru.chm.gradereport.ui.components.softFieldColors
import ru.chm.gradereport.ui.components.softHoverable
import ru.chm.gradereport.ui.theme.GradeTheme
import ru.chm.gradereport.ui.theme.YellowAccent

@OptIn(ExperimentalLayoutApi::class)
@Composable
internal fun GradeEditDialog(
    target: GradeEditTarget,
    submissions: List<HomeworkSubmissionItemDto> = emptyList(),
    canEditSubmissions: Boolean = false,
    onEditSubmission: (submissionId: String, url: String) -> Unit = { _, _ -> },
    onDeleteSubmission: (submissionId: String) -> Unit = {},
    onDismiss: () -> Unit,
    onSave: (Double?) -> Unit,
    onClear: () -> Unit,
) {
    val options = gradeOptions(target.maxScore)

    Dialog(onDismissRequest = onDismiss) {
        AppCard(modifier = Modifier.widthIn(min = 320.dp, max = 420.dp)) {
            Text("Оценка", color = GradeTheme.colors.textPrimary, fontWeight = FontWeight.Bold, fontSize = 20.sp)
            MutedText(target.studentName, modifier = Modifier.padding(top = 4.dp))
            Text(
                target.homeworkTitle,
                color = YellowAccent,
                fontSize = 15.sp,
                modifier = Modifier.padding(top = 2.dp, bottom = 14.dp),
            )

            FlowRow(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(8.dp),
                verticalArrangement = Arrangement.spacedBy(8.dp),
            ) {
                options.forEach { score ->
                    GradeOptionChip(
                        score = score,
                        selected = target.current?.let { it.toInt() == score } == true,
                        onClick = { onSave(score.toDouble()) },
                    )
                }
            }

            if (submissions.isNotEmpty()) {
                Text(
                    "Ответы на ДЗ",
                    color = GradeTheme.colors.textPrimary,
                    fontWeight = FontWeight.SemiBold,
                    fontSize = 15.sp,
                    modifier = Modifier.padding(top = 16.dp, bottom = 2.dp),
                )
                submissions.forEach { submission ->
                    SubmissionRow(
                        url = submission.url,
                        canEdit = canEditSubmissions,
                        onEdit = { url -> onEditSubmission(submission.id, url) },
                        onDelete = if (canEditSubmissions) {
                            { onDeleteSubmission(submission.id) }
                        } else {
                            null
                        },
                    )
                }
            }

            Row(
                modifier = Modifier.fillMaxWidth().padding(top = 16.dp),
                horizontalArrangement = Arrangement.spacedBy(8.dp),
            ) {
                SecondaryButton(text = "Отмена", onClick = onDismiss, modifier = Modifier.weight(1f))
                SecondaryButton(text = "Очистить", onClick = onClear, modifier = Modifier.weight(1f))
            }
        }
    }
}

@Composable
private fun GradeOptionChip(
    score: Int,
    selected: Boolean,
    onClick: () -> Unit,
) {
    val color = gradeColor(score.toDouble())
    val shape = RoundedCornerShape(12.dp)
    val hover = rememberSoftHover()
    val (bg, border) = softFieldColors(
        state = hover,
        idleBg = color.copy(alpha = if (selected) 0.28f else 0.14f),
        hoverBg = color.copy(alpha = if (selected) 0.4f else 0.26f),
        pressBg = color.copy(alpha = 0.46f),
        idleBorder = color.copy(alpha = if (selected) 0.9f else 0.45f),
        hoverBorder = color.copy(alpha = 0.95f),
        pressBorder = color,
    )

    Box(
        modifier = Modifier
            .size(56.dp)
            .softHoverable(hover)
            .background(bg, shape)
            .border(
                width = if (selected) 2.dp else 1.dp,
                color = border,
                shape = shape,
            )
            .clickable(
                interactionSource = hover.interactionSource,
                indication = null,
                onClick = onClick,
            ),
        contentAlignment = Alignment.Center,
    ) {
        Text(
            score.toString(),
            color = color,
            fontWeight = FontWeight.Bold,
            fontSize = 22.sp,
        )
    }
}
